import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import managedAxios from "@/utils/managedAxios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import {
  BookOpen, Target, Award, TrendingUp, ArrowRight, Sparkles,
  FileText, MapPin, BarChart3, CircleCheck, AlertCircle, Lightbulb, ArrowLeft,
  Search, CheckCircle2, PenTool, Scale, Beaker, Languages, Anchor,
  Send, Loader2, Star, AlertTriangle, ChevronRight, Clock
} from "lucide-react";

// ─── PAPER CONFIG ──────────────────────────────────────────────
const PAPER_CONFIG = {
  paper1: {
    name: "Paper I — General Essay",
    short: "Essay",
    icon: PenTool,
    color: "from-rose-500 to-pink-500",
    bgColor: "from-rose-50/30 to-pink-50/30",
    tipBg: "from-rose-100 to-pink-100",
    tipText: "text-rose-900",
    tipSubText: "text-rose-700",
    focus: "Current Affairs, Social & Cultural Themes",
    focusTip: "Write 3 essays per section. AP-specific examples score extra.",
    subjects: [
      { label: "Current Affairs", value: "current_affairs" },
      { label: "Socio-Political Issues", value: "socio_political" },
      { label: "Socio-Economic Issues", value: "socio_economic" },
      { label: "Socio-Environmental", value: "socio_environmental" },
      { label: "Cultural & Historical", value: "cultural_historical" },
      { label: "Civic Awareness", value: "civic_awareness" },
    ],
  },
  paper2: {
    name: "Paper II — History, Culture & Geography",
    short: "History & Culture",
    icon: BookOpen,
    color: "from-orange-500 to-amber-500",
    bgColor: "from-orange-50/30 to-amber-50/30",
    tipBg: "from-orange-100 to-amber-100",
    tipText: "text-orange-900",
    tipSubText: "text-orange-700",
    focus: "India & AP History, Culture, Geography",
    focusTip: "Satavahanas, Kakatiyas, Vijayanagara & AP post-bifurcation are high-yield.",
    subjects: [
      { label: "Ancient India & Andhra", value: "ancient_india_ap" },
      { label: "Medieval India & Andhra", value: "medieval_india_ap" },
      { label: "Modern India & National Movement", value: "modern_india" },
      { label: "AP History & Post-Bifurcation", value: "ap_history" },
      { label: "Geography of India & AP", value: "geography_india_ap" },
      { label: "Art, Architecture & Culture", value: "art_culture" },
    ],
  },
  paper3: {
    name: "Paper III — Polity, Governance, Law & Ethics",
    short: "Polity & Ethics",
    icon: Scale,
    color: "from-blue-500 to-indigo-500",
    bgColor: "from-blue-50/30 to-indigo-50/30",
    tipBg: "from-blue-100 to-indigo-100",
    tipText: "text-blue-900",
    tipSubText: "text-blue-700",
    focus: "Constitution, Governance, Ethics & Law",
    focusTip: "RTI, Lokpal, 73rd/74th Amendments & Ethics case studies are must-practice.",
    subjects: [
      { label: "Indian Constitution & Polity", value: "constitution_polity" },
      { label: "Governance & Public Admin", value: "governance_admin" },
      { label: "Ethics & Human Values", value: "ethics_values" },
      { label: "Law (Civil, Criminal, Cyber, Tax)", value: "law" },
      { label: "Local Governance (73rd/74th)", value: "local_governance" },
      { label: "E-Governance & RTI", value: "e_governance_rti" },
    ],
  },
  paper4: {
    name: "Paper IV — Economy & Development",
    short: "Economy",
    icon: TrendingUp,
    color: "from-emerald-500 to-teal-500",
    bgColor: "from-emerald-50/30 to-teal-50/30",
    tipBg: "from-emerald-100 to-teal-100",
    tipText: "text-emerald-900",
    tipSubText: "text-emerald-700",
    focus: "Indian & AP Economy, Budgeting, Industry",
    focusTip: "AP Budget, Industrial Corridors, GST & Make in India are recurring themes.",
    subjects: [
      { label: "Indian Economy Challenges", value: "indian_economy" },
      { label: "AP Economy & Resources", value: "ap_economy" },
      { label: "Government Budgeting", value: "budgeting" },
      { label: "Inclusive Growth & Welfare", value: "inclusive_growth" },
      { label: "Agriculture & Rural Dev", value: "agriculture" },
      { label: "Industrial Policy & Infra", value: "industrial_infra" },
    ],
  },
  paper5: {
    name: "Paper V — Science & Technology",
    short: "Sci & Tech",
    icon: Beaker,
    color: "from-cyan-500 to-blue-500",
    bgColor: "from-cyan-50/30 to-blue-50/30",
    tipBg: "from-cyan-100 to-blue-100",
    tipText: "text-cyan-900",
    tipSubText: "text-cyan-700",
    focus: "S&T, Space, Energy, Environment, Biotech",
    focusTip: "ISRO achievements, Climate Change, Renewable Energy & Cyber Laws are high-yield.",
    subjects: [
      { label: "S&T Integration & Innovation", value: "st_innovation" },
      { label: "ICT & E-Governance", value: "ict_egovernance" },
      { label: "Space & Defence (ISRO/DRDO)", value: "space_defence" },
      { label: "Energy & Nuclear Policy", value: "energy_nuclear" },
      { label: "Environment & Sustainability", value: "environment" },
      { label: "Biotech & Nanotech", value: "biotech_nanotech" },
    ],
  },
  english: {
    name: "English Language Paper",
    short: "English",
    icon: FileText,
    color: "from-violet-500 to-purple-500",
    bgColor: "from-violet-50/30 to-purple-50/30",
    tipBg: "from-violet-100 to-purple-100",
    tipText: "text-violet-900",
    tipSubText: "text-violet-700",
    focus: "Essay, Letter, Precis, Grammar",
    focusTip: "Practice essay + letter daily. Grammar MCQs need drilling.",
    subjects: [
      { label: "Essay Writing", value: "essay" },
      { label: "Letter & Report Writing", value: "letter_report" },
      { label: "Precis & Comprehension", value: "precis_comprehension" },
      { label: "Grammar", value: "grammar" },
      { label: "Translation", value: "translation" },
    ],
  },
  telugu: {
    name: "Telugu Language Paper",
    short: "Telugu",
    icon: Languages,
    color: "from-amber-500 to-orange-500",
    bgColor: "from-amber-50/30 to-orange-50/30",
    tipBg: "from-amber-100 to-orange-100",
    tipText: "text-amber-900",
    tipSubText: "text-amber-700",
    focus: "Vyasam, Precis, Grammar & Translation",
    focusTip: "Telugu grammar and essay practice are critical for scoring.",
    subjects: [
      { label: "Essay (Vyasam)", value: "essay_telugu" },
      { label: "Precis & Comprehension", value: "precis_telugu" },
      { label: "Letter & Report", value: "letter_report_telugu" },
      { label: "Grammar", value: "grammar_telugu" },
      { label: "Translation", value: "translation_telugu" },
    ],
  },
};

const APPSCPaperSuitePage = () => {
  const { paperId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState("home");
  const [loading, setLoading] = useState(false);

  // Answer Writing state
  const [question, setQuestion] = useState("");
  const [marks, setMarks] = useState(15);
  const [wordLimit, setWordLimit] = useState(225);
  const [answer, setAnswer] = useState(null);

  // Evaluation state
  const [evalQuestion, setEvalQuestion] = useState("");
  const [evalAnswer, setEvalAnswer] = useState("");
  const [evaluation, setEvaluation] = useState(null);

  // Expected Questions state
  const [subject, setSubject] = useState("");
  const [expectedData, setExpectedData] = useState(null);

  // PYQ Pattern state
  const [pyqSubject, setPyqSubject] = useState("");
  const [pyqYearRange, setPyqYearRange] = useState("5");
  const [pyqData, setPyqData] = useState(null);

  const isPro = user?.role === "PRO";
  const paper = PAPER_CONFIG[paperId];

  // Set default subjects when paper changes
  useEffect(() => {
    if (paper?.subjects?.length > 0) {
      setSubject(paper.subjects[0].value);
      setPyqSubject(paper.subjects[0].value);
    }
  }, [paperId]);

  if (!paper) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500">Paper not found</p>
          <Button onClick={() => navigate("/appsc/mains")} className="mt-4">Back to Mains Hub</Button>
        </div>
      </div>
    );
  }

  const PaperIcon = paper.icon;

  // ─── API CALLS ──────────────────────────────────────────────────
  const handleGenerateAnswer = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await managedAxios.post(`${API_BASE}/appsc/mains/generate-answer`, {
        paper_id: paperId,
        question,
        marks,
        word_limit: wordLimit,
        diagram_type: "Flowchart",
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAnswer(data);
      toast({ description: `${paper.short} answer generated!` });
    } catch (err) {
      toast({ variant: "destructive", description: err?.response?.data?.detail || "Could not generate answer." });
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluate = async (e) => {
    e.preventDefault();
    if (!evalQuestion.trim() || !evalAnswer.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await managedAxios.post(`${API_BASE}/appsc/mains/evaluate`, {
        paper_id: paperId,
        question: evalQuestion,
        answer: evalAnswer,
        marks,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEvaluation(data);
      toast({ description: `${paper.short} evaluation generated!` });
    } catch (err) {
      toast({ variant: "destructive", description: err?.response?.data?.detail || "Could not evaluate." });
    } finally {
      setLoading(false);
    }
  };

  const fetchExpectedQuestions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await managedAxios.post(`${API_BASE}/appsc/mains/expected-questions`, {
        paper_id: paperId,
        subject,
        num_questions: 10,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setExpectedData(data);
      toast({ description: "Expected questions generated!" });
    } catch (err) {
      toast({ variant: "destructive", description: "Could not load expected questions." });
    } finally {
      setLoading(false);
    }
  };

  const fetchPyqPattern = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await managedAxios.post(`${API_BASE}/appsc/mains/pyq-analyze`, {
        paper_id: paperId,
        subject: pyqSubject,
        year_range: parseInt(pyqYearRange),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPyqData(data);
      toast({ description: "PYQ analysis generated!" });
    } catch (err) {
      toast({ variant: "destructive", description: "Could not load PYQ pattern." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "expected-questions" && subject) fetchExpectedQuestions();
  }, [subject, mode]);

  useEffect(() => {
    if (mode === "pyq-pattern" && pyqSubject) fetchPyqPattern();
  }, [pyqSubject, mode, pyqYearRange]);

  // ═══════════════════════════════════════════════════════════
  // HOME VIEW
  // ═══════════════════════════════════════════════════════════
  if (mode === "home") {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-slate-50 via-${paper.bgColor}`}>
        {/* Header */}
        <div className={`bg-gradient-to-r ${paper.color} text-white`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button onClick={() => navigate("/appsc/mains")} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors" data-testid="paper-suite-back">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-2">
                    <PaperIcon className="w-7 h-7" />
                    {paper.short} Suite
                  </h1>
                  <p className="text-white/70 text-sm">{paper.focus}</p>
                </div>
              </div>
              <Badge className={`px-3 py-1.5 ${isPro ? 'bg-white/20 text-white border-0' : 'bg-white/10 text-white/70 border-0'}`}>
                {isPro ? 'PRO' : 'FREE'}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">{isPro ? '∞' : '1'}</p>
                <p className="text-[10px] text-white/70">Full Answers</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">∞</p>
                <p className="text-[10px] text-white/70">Evaluations</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">150</p>
                <p className="text-[10px] text-white/70">Marks</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {/* Topper Tip */}
          <Card className={`border-0 shadow-lg bg-gradient-to-r ${paper.tipBg} overflow-hidden`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${paper.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className={`font-semibold ${paper.tipText}`}>{paper.short} Focus Areas</p>
                  <p className={`text-sm ${paper.tipSubText}`}>{paper.focusTip}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 4 Feature Cards (2x2 Grid) */}
          <div className="grid grid-cols-2 gap-4">
            {/* Answer Writing */}
            <Card className={`border-2 border-slate-200 bg-gradient-to-br ${paper.bgColor} cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden`}
              onClick={() => setMode("answer-writing")} data-testid="feature-answer-writing">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 bg-gradient-to-br ${paper.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg`}>
                    <BookOpen className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-slate-800">Answer Writing</h3>
                    <p className="text-sm text-slate-600 mt-1">Generate model answers with structured format</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-slate-600">
                    Start Writing <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Smart Evaluation */}
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-500/10 to-pink-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("evaluation")} data-testid="feature-evaluation">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-slate-800">Smart Evaluation</h3>
                    <p className="text-sm text-slate-600 mt-1">Get APPSC-aligned feedback on your answers</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-purple-600">
                    Evaluate <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Expected Questions */}
            <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("expected-questions")} data-testid="feature-expected">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-slate-800">Expected Questions</h3>
                    <p className="text-sm text-slate-600 mt-1">AI-predicted questions for APPSC 2026</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-emerald-600">
                    Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PYQ Pattern */}
            <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("pyq-pattern")} data-testid="feature-pyq">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-slate-800">PYQ Pattern</h3>
                    <p className="text-sm text-slate-600 mt-1">Trend analysis & topic frequency insights</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-amber-600">
                    Analyze <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Info Row */}
          <div className="grid grid-cols-3 gap-3">
            {paper.subjects.slice(0, 3).map((s, idx) => (
              <Card key={idx} className="border border-slate-200 bg-white">
                <CardContent className="p-4 text-center">
                  <div className={`w-10 h-10 bg-gradient-to-br ${paper.color} rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md`}>
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-medium text-sm text-slate-800">{s.label}</p>
                  <p className="text-[10px] text-slate-500">High-yield topic</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {!isPro && (
            <Card className={`border-2 border-slate-300 bg-gradient-to-r from-slate-50 to-blue-50 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow`}
              onClick={() => navigate("/upgrade")}>
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 bg-gradient-to-br ${paper.color} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-lg text-slate-800">Upgrade to CLARIAS PRO</p>
                    <p className="text-sm text-slate-600">Unlock unlimited {paper.short} answers & evaluations</p>
                  </div>
                  <ArrowRight className="w-6 h-6 text-slate-500" />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // ANSWER WRITING VIEW
  // ═══════════════════════════════════════════════════════════
  if (mode === "answer-writing") {
    return (
      <div className="min-h-screen bg-slate-50/30">
        <div className="relative max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="flex items-center justify-between">
            <Button variant="outline" className="rounded-full" onClick={() => { setMode("home"); setAnswer(null); }} data-testid="aw-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Badge className={`px-3 py-1 ${isPro ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0' : 'bg-slate-100 text-slate-600 border-0'}`}>
              {isPro ? 'PRO' : 'FREE'}
            </Badge>
          </div>

          <div className={`bg-gradient-to-r ${paper.color} rounded-2xl p-6 text-white`}>
            <h1 className="text-2xl font-bold flex items-center gap-2"><PaperIcon className="w-7 h-7" /> {paper.short} Answer Writing</h1>
            <p className="text-white/70 mt-1">{paper.focus}</p>
          </div>

          <Card className="border-slate-200 bg-white">
            <CardContent className="p-6">
              <form className="space-y-5" onSubmit={handleGenerateAnswer}>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">QUESTION</label>
                  <Textarea rows={4} value={question} onChange={(e) => setQuestion(e.target.value)}
                    placeholder={`Enter your ${paper.short} question...`} required
                    className="text-sm resize-none border-2 focus:border-blue-400" data-testid="aw-question" />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">MARKS</label>
                    <div className="flex gap-2">
                      {[10, 15, 20, 25].map((m) => (
                        <Button key={m} type="button" variant={marks === m ? "default" : "outline"} size="sm"
                          onClick={() => { setMarks(m); setWordLimit(m * 15); }}
                          className={marks === m ? `bg-gradient-to-r ${paper.color} text-white` : ""}>
                          {m}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">WORD LIMIT</label>
                    <input type="number" value={wordLimit} onChange={(e) => setWordLimit(parseInt(e.target.value) || 150)}
                      className="w-full px-3 py-2 text-sm border-2 border-slate-200 rounded-md focus:border-blue-400 focus:outline-none"
                      min="50" max="500" />
                  </div>
                </div>
                <Button type="submit" disabled={loading}
                  className={`w-full font-semibold py-3 bg-gradient-to-r ${paper.color} hover:opacity-90 text-white`}
                  data-testid="aw-generate">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</> : <><Send className="w-4 h-4 mr-2" /> Generate {paper.short} Answer</>}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Display Answer */}
          {answer && (
            <div className="space-y-4" data-testid="aw-result">
              {answer.interpretation && (
                <Card className="border-slate-200"><CardContent className="p-5">
                  <h3 className="text-sm font-bold text-purple-900 uppercase mb-2">Question Interpretation</h3>
                  <p className="text-sm text-slate-800">{answer.interpretation}</p>
                </CardContent></Card>
              )}
              {answer.intro && (
                <Card className="border-emerald-200 bg-emerald-50/50"><CardContent className="p-5">
                  <h3 className="text-sm font-bold text-purple-900 uppercase mb-2">Introduction</h3>
                  <p className="text-sm text-slate-800 leading-relaxed">{answer.intro}</p>
                </CardContent></Card>
              )}
              {answer.body?.length > 0 && (
                <Card className="border-pink-200 bg-pink-50/50"><CardContent className="p-5">
                  <h3 className="text-sm font-bold text-purple-900 uppercase mb-4">Body</h3>
                  <div className="space-y-4">
                    {answer.body.map((section, idx) => (
                      <div key={idx} className="space-y-2">
                        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-3 border border-purple-200">
                          <h4 className="text-sm font-bold text-purple-900">Part {String.fromCharCode(65 + idx)}: {section.heading}</h4>
                        </div>
                        {section.points?.map((point, pidx) => (
                          <div key={pidx} className="bg-white rounded-xl border-2 border-slate-200 p-4 space-y-3">
                            <h5 className="text-sm font-bold text-slate-900 uppercase">{point.keyword}</h5>
                            <p className="text-sm text-slate-700 leading-relaxed">{point.explanation}</p>
                            {point.example && (
                              <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-lg p-2">
                                <p className="text-xs text-blue-800"><span className="font-semibold">Example:</span> {point.example}</p>
                              </div>
                            )}
                            {point.stat && (
                              <div className="bg-green-50 border-l-4 border-green-400 rounded-r-lg p-2">
                                <p className="text-xs text-green-800"><span className="font-semibold">Data:</span> {point.stat}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </CardContent></Card>
              )}
              {answer.way_forward?.length > 0 && (
                <Card className="border-yellow-200 bg-yellow-50"><CardContent className="p-5">
                  <h3 className="text-sm font-bold text-purple-900 uppercase mb-3">Way Forward</h3>
                  <ol className="space-y-2">
                    {answer.way_forward.map((item, idx) => (
                      <li key={idx} className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 bg-amber-100 text-amber-900 rounded-full flex items-center justify-center text-xs font-bold">{idx + 1}</span><span className="text-sm text-slate-800">{item}</span></li>
                    ))}
                  </ol>
                </CardContent></Card>
              )}
              {answer.conclusion && (
                <Card className="border-slate-200"><CardContent className="p-5">
                  <h3 className="text-sm font-bold text-purple-900 uppercase mb-2">Conclusion</h3>
                  <p className="text-sm text-slate-800 leading-relaxed">{answer.conclusion}</p>
                </CardContent></Card>
              )}
              {answer.topper_notes?.bullets?.length > 0 && (
                <Card className="border-amber-200 bg-amber-50"><CardContent className="p-5">
                  <h3 className="text-sm font-bold text-amber-900 uppercase mb-3">Topper Notes</h3>
                  <ul className="space-y-2">
                    {answer.topper_notes.bullets.map((note, idx) => (
                      <li key={idx} className="flex gap-3 items-start text-sm text-slate-800"><Star className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />{note}</li>
                    ))}
                  </ul>
                </CardContent></Card>
              )}
              {answer.checklist?.length > 0 && (
                <Card className="border-green-200 bg-green-50"><CardContent className="p-5">
                  <h3 className="text-sm font-bold text-green-900 uppercase mb-3">Checklist</h3>
                  <div className="space-y-2">
                    {answer.checklist.map((item, idx) => (
                      <div key={idx} className="bg-white border border-green-200 rounded-lg p-3 flex items-center gap-3">
                        {item.present ? <CircleCheck className="w-5 h-5 text-green-600 shrink-0" /> : <AlertCircle className="w-5 h-5 text-slate-400 shrink-0" />}
                        <span className="text-sm text-slate-800">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </CardContent></Card>
              )}
              {answer.score_estimate !== undefined && (
                <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50"><CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Award className="w-8 h-8 text-blue-600" />
                      <div><h3 className="text-sm font-bold text-blue-900">Score Estimate</h3><p className="text-xs text-blue-600">Based on APPSC standards</p></div>
                    </div>
                    <div className="bg-white rounded-xl px-4 py-2 border-2 border-blue-200">
                      <p className="text-3xl font-bold text-blue-900">{answer.score_estimate}/10</p>
                    </div>
                  </div>
                </CardContent></Card>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // EVALUATION VIEW
  // ═══════════════════════════════════════════════════════════
  if (mode === "evaluation") {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => { setMode("home"); setEvaluation(null); }} data-testid="eval-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to {paper.short} Suite
          </Button>
        </div>

        <div className={`bg-gradient-to-r ${paper.color} rounded-2xl p-8 text-white`}>
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3"><Sparkles className="w-8 h-8" /> {paper.short} Smart Evaluation</h1>
          <p className="text-white/70">Get APPSC-specific feedback on your {paper.short} answers</p>
        </div>

        {/* Features Grid */}
        <div className="grid gap-4 md:grid-cols-4">
          {[
            { icon: FileText, color: "border-blue-200 bg-blue-50", iconColor: "text-blue-600", title: "Structure Audit", sub: "Flow & organization" },
            { icon: Target, color: "border-emerald-200 bg-emerald-50", iconColor: "text-emerald-600", title: "Depth Analysis", sub: "Coverage & dimensions" },
            { icon: Lightbulb, color: "border-purple-200 bg-purple-50", iconColor: "text-purple-600", title: "Keyword Enhancement", sub: "Missing terms & data" },
            { icon: Award, color: "border-amber-200 bg-amber-50", iconColor: "text-amber-600", title: "Score Estimate", sub: "APPSC-style marking" },
          ].map((f, idx) => (
            <Card key={idx} className={`${f.color}`}><CardContent className="p-4 space-y-2">
              <f.icon className={`w-5 h-5 ${f.iconColor}`} />
              <p className="text-xs font-semibold text-slate-900">{f.title}</p>
              <p className="text-[11px] text-slate-600">{f.sub}</p>
            </CardContent></Card>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-2 items-start">
          <Card className="border-slate-200 bg-white shadow-sm"><CardContent className="p-6 space-y-5">
            <h2 className="text-lg font-bold text-slate-900">Submit Your {paper.short} Answer</h2>
            <form className="space-y-4" onSubmit={handleEvaluate}>
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase text-slate-700">QUESTION</p>
                <Textarea rows={3} value={evalQuestion} onChange={(e) => setEvalQuestion(e.target.value)}
                  placeholder={`Paste the ${paper.short} question you attempted...`} required className="text-sm resize-none" data-testid="eval-question" />
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase text-slate-700">YOUR ANSWER</p>
                <Textarea rows={10} value={evalAnswer} onChange={(e) => setEvalAnswer(e.target.value)}
                  placeholder={`Paste or type your ${paper.short} answer...`} required className="text-sm resize-none" data-testid="eval-answer" />
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase text-slate-700">MARKS</p>
                <div className="flex gap-2">
                  {[10, 15, 20].map((m) => (
                    <Button key={m} type="button" variant={marks === m ? "default" : "outline"} size="sm"
                      onClick={() => setMarks(m)} className={marks === m ? `bg-gradient-to-r ${paper.color} text-white` : ""}>
                      {m}
                    </Button>
                  ))}
                </div>
              </div>
              <Button type="submit" className={`w-full bg-gradient-to-r ${paper.color} text-white`} disabled={loading} data-testid="eval-submit">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Evaluating...</> : <><Target className="w-4 h-4 mr-2" /> Evaluate My Answer</>}
              </Button>
            </form>
          </CardContent></Card>

          {/* Evaluation Results */}
          {!evaluation && !loading && (
            <Card className="border-dashed border-slate-300 bg-slate-50/50"><CardContent className="p-8 text-center space-y-4">
              <div className={`bg-gradient-to-br ${paper.tipBg} p-4 rounded-full inline-block`}><Target className="w-8 h-8 text-slate-600" /></div>
              <p className="font-bold text-slate-900">Your Evaluation Will Appear Here</p>
              <p className="text-sm text-slate-600">Submit your answer to receive AI-powered feedback</p>
            </CardContent></Card>
          )}

          {evaluation && (
            <Card className="border-slate-200 bg-white shadow-sm"><CardContent className="p-6 space-y-5" data-testid="eval-result">
              <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">{paper.short} Evaluation</h2>
                {typeof evaluation.score_estimate === "number" && (
                  <div className={`bg-gradient-to-r ${paper.color} text-white px-4 py-2 rounded-xl text-center`}>
                    <p className="text-xs font-semibold">Score</p>
                    <p className="text-2xl font-bold">{evaluation.score_estimate}/{marks}</p>
                  </div>
                )}
              </div>

              {/* Detailed Scores */}
              {evaluation.scores && Object.keys(evaluation.scores).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(evaluation.scores).map(([criterion, score]) => (
                    <div key={criterion} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <span className="text-xs font-medium text-slate-700 capitalize">{criterion.replace(/_/g, " ")}</span>
                      <Badge variant="outline" className="text-xs">{score}/10</Badge>
                    </div>
                  ))}
                </div>
              )}

              {/* Strengths & Weaknesses */}
              {(evaluation.strongest_areas?.length > 0 || evaluation.weakest_areas?.length > 0) && (
                <div className="grid md:grid-cols-2 gap-4">
                  {evaluation.strongest_areas?.length > 0 && (
                    <Card className="border-green-200 bg-green-50"><CardContent className="p-4 space-y-2">
                      <p className="text-sm font-bold text-green-900 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Strongest Areas</p>
                      <ul className="space-y-1.5">{evaluation.strongest_areas.map((a, i) => <li key={i} className="text-xs text-green-800 flex items-start gap-2"><span className="text-green-600">✓</span>{a}</li>)}</ul>
                    </CardContent></Card>
                  )}
                  {evaluation.weakest_areas?.length > 0 && (
                    <Card className="border-amber-200 bg-amber-50"><CardContent className="p-4 space-y-2">
                      <p className="text-sm font-bold text-amber-900 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Areas to Improve</p>
                      <ul className="space-y-1.5">{evaluation.weakest_areas.map((a, i) => <li key={i} className="text-xs text-amber-800 flex items-start gap-2"><span className="text-amber-600">!</span>{a}</li>)}</ul>
                    </CardContent></Card>
                  )}
                </div>
              )}

              {/* Structure & Depth Feedback */}
              {evaluation.structure_feedback?.length > 0 && (
                <Card className="border-blue-200 bg-blue-50"><CardContent className="p-4 space-y-2">
                  <p className="text-sm font-bold text-blue-900 flex items-center gap-2"><FileText className="w-4 h-4" /> Structure & Flow</p>
                  <ul className="space-y-1.5">{evaluation.structure_feedback.map((p, i) => <li key={i} className="text-xs text-slate-700 flex items-start gap-2"><CircleCheck className="w-3 h-3 text-blue-600 shrink-0 mt-0.5" />{p.comment}</li>)}</ul>
                </CardContent></Card>
              )}

              {evaluation.keyword_suggestions?.length > 0 && (
                <Card className="border-purple-200 bg-purple-50"><CardContent className="p-4 space-y-2">
                  <p className="text-sm font-bold text-purple-900 flex items-center gap-2"><Lightbulb className="w-4 h-4" /> Keywords to Add</p>
                  <div className="flex flex-wrap gap-2">{evaluation.keyword_suggestions.map((k, i) => <Badge key={i} className="bg-purple-100 text-purple-900 text-xs border-purple-300">{k}</Badge>)}</div>
                </CardContent></Card>
              )}

              {evaluation.better_intro && (
                <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-4">
                  <p className="text-sm font-bold text-emerald-900 mb-2">Improved Introduction</p>
                  <p className="text-sm text-slate-700">{evaluation.better_intro}</p>
                </CardContent></Card>
              )}

              {evaluation.meta_tips?.length > 0 && (
                <Card className="border-slate-200"><CardContent className="p-4">
                  <p className="text-sm font-bold text-slate-900 mb-2">Meta Tips</p>
                  <ul className="space-y-1.5">{evaluation.meta_tips.map((t, i) => <li key={i} className="text-xs text-slate-700 flex items-start gap-2"><Sparkles className="w-3 h-3 text-indigo-500 shrink-0 mt-0.5" />{t}</li>)}</ul>
                </CardContent></Card>
              )}
            </CardContent></Card>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // EXPECTED QUESTIONS VIEW
  // ═══════════════════════════════════════════════════════════
  if (mode === "expected-questions") {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => { setMode("home"); setExpectedData(null); }} data-testid="eq-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to {paper.short} Suite
          </Button>
        </div>

        <div className={`bg-gradient-to-r ${paper.color} rounded-2xl p-6 text-white`}>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Target className="w-7 h-7" /> {paper.short} Expected Questions</h1>
          <p className="text-white/70 mt-1">AI-predicted questions for APPSC 2026</p>
        </div>

        {/* Subject Selector */}
        <div className="flex flex-wrap gap-2">
          {paper.subjects.map((s) => (
            <button key={s.value} onClick={() => setSubject(s.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${subject === s.value ? `bg-gradient-to-r ${paper.color} text-white shadow-md` : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-400'}`}
              data-testid={`eq-subject-${s.value}`}>
              {s.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-500">Generating expected questions...</p>
          </div>
        )}

        {expectedData?.questions?.length > 0 && !loading && (
          <div className="space-y-3" data-testid="eq-results">
            {expectedData.analysis && (
              <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-4">
                <p className="text-sm font-bold text-emerald-900 mb-2">Analysis Summary</p>
                {expectedData.analysis.hot_topics?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {expectedData.analysis.hot_topics.map((t, i) => <Badge key={i} className="bg-emerald-100 text-emerald-800 text-xs">{t}</Badge>)}
                  </div>
                )}
                <p className="text-xs text-emerald-700">Confidence: {expectedData.analysis.prediction_confidence || "Medium"}</p>
              </CardContent></Card>
            )}

            {expectedData.questions.map((q, idx) => (
              <Card key={idx} className="border-slate-200 hover:shadow-lg transition-all hover:border-blue-300">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${paper.color} flex items-center justify-center shrink-0 text-white font-bold text-sm`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900 mb-2">{q.question}</p>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">{q.marks || 15}M</Badge>
                        <Badge variant="outline" className="text-xs">{q.difficulty || "Medium"}</Badge>
                        <Badge className={`text-xs ${q.probability === 'High' ? 'bg-red-100 text-red-700' : q.probability === 'Very High' ? 'bg-red-200 text-red-800' : 'bg-amber-100 text-amber-700'} border-0`}>
                          {q.probability || "Medium"} Probability
                        </Badge>
                      </div>
                      {q.approach_hint && <p className="text-xs text-slate-500 italic">{q.approach_hint}</p>}
                      <Button size="sm" variant="outline" className="mt-3"
                        onClick={() => { setQuestion(q.question); setMarks(q.marks || 15); setMode("answer-writing"); }}>
                        <PenTool className="w-3 h-3 mr-1" /> Practice This
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // PYQ PATTERN VIEW
  // ═══════════════════════════════════════════════════════════
  if (mode === "pyq-pattern") {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => { setMode("home"); setPyqData(null); }} data-testid="pyq-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to {paper.short} Suite
          </Button>
        </div>

        <div className={`bg-gradient-to-r ${paper.color} rounded-2xl p-6 text-white`}>
          <h1 className="text-2xl font-bold flex items-center gap-2"><TrendingUp className="w-7 h-7" /> {paper.short} PYQ Pattern Analysis</h1>
          <p className="text-white/70 mt-1">Previous year trend analysis & forecasting</p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex flex-wrap gap-2 flex-1">
            {paper.subjects.map((s) => (
              <button key={s.value} onClick={() => setPyqSubject(s.value)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${pyqSubject === s.value ? `bg-gradient-to-r ${paper.color} text-white shadow-md` : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-400'}`}>
                {s.label}
              </button>
            ))}
          </div>
          <select value={pyqYearRange} onChange={(e) => setPyqYearRange(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
            <option value="5">Last 5 years</option>
            <option value="10">Last 10 years</option>
            <option value="15">Last 15 years</option>
          </select>
        </div>

        {loading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-500">Analyzing PYQ patterns...</p>
          </div>
        )}

        {pyqData && !loading && (
          <div className="space-y-4" data-testid="pyq-results">
            {/* Topic Analysis Summary */}
            {pyqData.topic_analysis && (
              <Card className="border-blue-200 bg-blue-50"><CardContent className="p-5">
                <h3 className="font-bold text-blue-900 mb-3">Topic Analysis</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-900">{pyqData.topic_analysis.total_questions_found || 0}</p>
                    <p className="text-xs text-blue-600">Total Questions</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-900">{pyqData.topic_analysis.average_per_year || 0}</p>
                    <p className="text-xs text-blue-600">Avg/Year</p>
                  </div>
                  <div className="text-center">
                    <Badge className={`${pyqData.topic_analysis.trend === 'Increasing' ? 'bg-green-100 text-green-700' : pyqData.topic_analysis.trend === 'Decreasing' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} text-sm`}>
                      {pyqData.topic_analysis.trend || "Stable"}
                    </Badge>
                    <p className="text-xs text-blue-600 mt-1">Trend</p>
                  </div>
                </div>
              </CardContent></Card>
            )}

            {/* Year-wise breakdown */}
            {pyqData.year_wise?.length > 0 && (
              <Card className="border-slate-200"><CardContent className="p-5">
                <h3 className="font-bold text-slate-900 mb-3">Year-wise Breakdown</h3>
                <div className="space-y-2">
                  {pyqData.year_wise.map((y, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                      <Badge variant="outline" className="font-bold">{y.year}</Badge>
                      <div className="flex-1">
                        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                          <div className={`h-full bg-gradient-to-r ${paper.color} rounded-full`}
                            style={{ width: `${Math.min((y.questions || 0) * 15, 100)}%` }} />
                        </div>
                      </div>
                      <span className="text-sm font-medium text-slate-700">{y.questions} Qs</span>
                      <div className="flex flex-wrap gap-1">
                        {y.key_topics?.slice(0, 2).map((t, ti) => <Badge key={ti} className="bg-slate-100 text-slate-600 text-[10px] border-0">{t}</Badge>)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
            )}

            {/* Frequent Themes */}
            {pyqData.frequent_themes?.length > 0 && (
              <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-5">
                <h3 className="font-bold text-emerald-900 mb-3">Most Frequent Themes</h3>
                <div className="space-y-3">
                  {pyqData.frequent_themes.map((th, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 border border-emerald-200 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{th.theme}</p>
                        <p className="text-xs text-slate-500">Last asked: {th.last_asked} | Frequency: {th.frequency}x</p>
                      </div>
                      <Badge className={`${th.prediction?.includes('Very') ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} text-xs`}>
                        {th.prediction}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
            )}

            {/* Hot Predictions */}
            {pyqData.hot_predictions?.length > 0 && (
              <Card className="border-red-200 bg-red-50"><CardContent className="p-5">
                <h3 className="font-bold text-red-900 mb-3 flex items-center gap-2"><Target className="w-5 h-5" /> Hot Predictions for 2026</h3>
                <div className="space-y-2">
                  {pyqData.hot_predictions.map((hp, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 border border-red-200">
                      <p className="text-sm font-medium text-slate-900">{hp.topic}</p>
                      <p className="text-xs text-slate-500 mt-1">{hp.reason}</p>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
            )}

            {pyqData.approach_tips?.length > 0 && (
              <Card className="border-amber-200 bg-amber-50"><CardContent className="p-5">
                <h3 className="font-bold text-amber-900 mb-3">Approach Tips</h3>
                <ul className="space-y-2">
                  {pyqData.approach_tips.map((t, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2"><Sparkles className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />{t}</li>
                  ))}
                </ul>
              </CardContent></Card>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default APPSCPaperSuitePage;
